/* eslint-disable import/prefer-default-export */
import React from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { useModal } from '@gsa/afp-component-library';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import AddContractLineModal from './add-contract-line/add-contract-line-modal';
import ImportContractLineModal from './import-contract-line/import-contract-line-modal';
import { AddContractLineProvider } from './add-contract-line/add-contract-line-provider';
import ErrorModal from './error-modal';
import { OPERATIONS, SUBJECTS } from '../../../utilities/constants';
import { isContractSOP } from '../components/contract-helpers';
import { CONTRACT_ACTIONS, ContractActions } from '../contract-actions';

export const ContractLineListingActions = ({
  contractHeader = {},
  setAlert,
  refetchLines,
  refetchHeader,
  refetchCarryoverLines,
  editable = false,
}) => {
  const { contractHeaderId } = useParams();

  const isSop = isContractSOP(contractHeader?.solicitation?.purchaseTypeCode);

  const {
    isOpen: isAddContractLineModalOpen,
    openModal: setAddContractLineModalOpen,
    closeModal: setAddContractLineModalClose,
  } = useModal();

  const {
    isOpen: isImportContractLineModalOpen,
    openModal: openImportContractLineModal,
    closeModal: closeImportContractLineModal,
  } = useModal();

  const {
    isOpen: isErrorModalOpen,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModal();

  const ability = useAppAbility();

  const canUpdateContract =
    editable && ability.can(OPERATIONS.Update, SUBJECTS.Contract);

  const refetchAll = () => {
    refetchHeader();
    refetchLines();
    refetchCarryoverLines();
  };

  return (
    <>
      <ContractActions
        actions={[
          CONTRACT_ACTIONS.VIEW_CONTRACT,
          canUpdateContract && {
            ...CONTRACT_ACTIONS.ADD_CONTRACT_LINE,
            onClickHandler: () => {
              if (!isSop && !contractHeader.publishedInFleet) {
                openErrorModal(true);
              } else {
                setAddContractLineModalOpen(true);
              }
            },
          },
          isSop &&
            canUpdateContract && {
              ...CONTRACT_ACTIONS.IMPORT_AWARDED_BID_LINES,
              onClickHandler: openImportContractLineModal,
            },
        ]}
        contractHeaderId={contractHeaderId}
      />
      {isAddContractLineModalOpen && (
        <AddContractLineProvider>
          <AddContractLineModal
            isOpen={isAddContractLineModalOpen}
            onClose={setAddContractLineModalClose}
            contractHeader={contractHeader}
            onCreateSuccess={(contractLine) => {
              setAddContractLineModalClose();
              setAlert({
                type: 'success',
                message: (
                  <>
                    Line item
                    <Link
                      to={`/catalog/contract/${contractHeaderId}/contract-line-template/${contractLine.id}`}
                      className="text-bold margin-left-1"
                    >
                      {contractLine.scheduleLine}
                    </Link>{' '}
                    has been added.
                  </>
                ),
              });
              refetchLines();
              refetchCarryoverLines();
            }}
          />
        </AddContractLineProvider>
      )}
      <ImportContractLineModal
        isOpen={isImportContractLineModalOpen}
        contractHeader={contractHeader}
        onClose={closeImportContractLineModal}
        setAlert={setAlert}
        refetch={refetchAll}
      />
      {isErrorModalOpen && (
        <ErrorModal closeModal={closeErrorModal} isOpen={isErrorModalOpen} />
      )}
    </>
  );
};

ContractLineListingActions.propTypes = {
  contractHeader: PropTypes.shape({
    solicitation: PropTypes.shape({
      purchaseTypeCode: PropTypes.string,
      solicitationNumber: PropTypes.string,
    }),
    publishedInFleet: PropTypes.bool,
  }),
  setAlert: PropTypes.func.isRequired,
  refetchLines: PropTypes.func.isRequired,
  refetchHeader: PropTypes.func.isRequired,
  refetchCarryoverLines: PropTypes.func.isRequired,
};
